import { isNotNullOrEmpty, isNullOrWhiteSpace, reject } from "../../../../Common/TypingUtils";

export class ValidationHelper {
    public static isBlank(str: undefined | null | string): boolean {
        return !str || /^\s*$/.test(str);
    }

    public static isNumber(str: string | undefined | null): boolean {
        return !this.isBlank(str) && str != undefined && /^-?\d+(([.]\d+$)|($))/.test(str);
    }

    public static isDateFormat(date: string): boolean {
        if (/^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[012])\.(19|20)[0-9]{2}$/.test(date)) {
            const v = date.split(".");
            const d = parseInt(v[0] ?? reject(), 10);
            const m = parseInt(v[1] ?? reject(), 10);
            const y = parseInt(v[2] ?? reject(), 10);
            const o = new Date(Date.UTC(y, m - 1, d));

            return o.getUTCDate() === d && o.getUTCMonth() + 1 === m && o.getUTCFullYear() === y;
        }

        return false;
    }

    public static isDateFormatIso(date: string): boolean {
        if (/^(19|20)[0-9]{2}\-(0[1-9]|1[012])\-(0[1-9]|[12][0-9]|3[01])$/.test(date)) {
            const v = date.split("-");
            const d = parseInt(v[2] ?? reject(), 10);
            const m = parseInt(v[1] ?? reject(), 10);
            const y = parseInt(v[0] ?? reject(), 10);
            const o = new Date(Date.UTC(y, m - 1, d));

            return o.getUTCDate() === d && o.getUTCMonth() + 1 === m && o.getUTCFullYear() === y;
        }

        return false;
    }

    public static isInn(str: undefined | null | string): boolean {
        if (isNullOrWhiteSpace(str)) {
            return false;
        }
        const innBits: number[] = str.split("").map(x => parseInt(x, 10));
        if (innBits.length === 10) {
            const sum9Inn =
                (innBits[0] ?? reject()) * 2 +
                (innBits[1] ?? reject()) * 4 +
                (innBits[2] ?? reject()) * 10 +
                (innBits[3] ?? reject()) * 3 +
                (innBits[4] ?? reject()) * 5 +
                (innBits[5] ?? reject()) * 9 +
                (innBits[6] ?? reject()) * 4 +
                (innBits[7] ?? reject()) * 6 +
                (innBits[8] ?? reject()) * 8;
            const k10 = (sum9Inn % 11) % 10;

            return k10 === innBits[9];
        }
        if (innBits.length === 12) {
            const sum10Inn =
                (innBits[0] ?? reject()) * 7 +
                (innBits[1] ?? reject()) * 2 +
                (innBits[2] ?? reject()) * 4 +
                (innBits[3] ?? reject()) * 10 +
                (innBits[4] ?? reject()) * 3 +
                (innBits[5] ?? reject()) * 5 +
                (innBits[6] ?? reject()) * 9 +
                (innBits[7] ?? reject()) * 4 +
                (innBits[8] ?? reject()) * 6 +
                (innBits[9] ?? reject()) * 8;
            const k11 = (sum10Inn % 11) % 10;
            const sum11Inn =
                (innBits[0] ?? reject()) * 3 +
                (innBits[1] ?? reject()) * 7 +
                (innBits[2] ?? reject()) * 2 +
                (innBits[3] ?? reject()) * 4 +
                (innBits[4] ?? reject()) * 10 +
                (innBits[5] ?? reject()) * 3 +
                (innBits[6] ?? reject()) * 5 +
                (innBits[7] ?? reject()) * 9 +
                (innBits[8] ?? reject()) * 4 +
                (innBits[9] ?? reject()) * 6 +
                (innBits[10] ?? reject()) * 8;
            const k12 = (sum11Inn % 11) % 10;

            return k11 === innBits[10] && k12 === innBits[11];
        }

        return false;
    }

    public static isOgrn(value: string | undefined): boolean {
        if (isNullOrWhiteSpace(value)) {
            return false;
        }

        const hasAppropriateLength = [PHYS_OGRN_LENGTH, IP_OGRN_LENGTH].includes(value.length);
        if (!hasAppropriateLength) {
            return false;
        }

        const matchingPattern = ogrnPattern.test(value);
        if (!matchingPattern) {
            return false;
        }

        const lastCharIndex = value.length - 1;
        const divisible = Number(value.slice(0, lastCharIndex));

        const divider = lastCharIndex - 1;
        const checkingDigit = (divisible % divider) % 10;

        const lastDigit = Number(value[lastCharIndex]);
        const isOgrn = checkingDigit == lastDigit;

        return isOgrn;
    }

    public static isValidAccountNumber(bik: string | undefined, accountNumber: string | undefined): boolean {
        if (isNullOrWhiteSpace(bik) || isNullOrWhiteSpace(accountNumber)) {
            return false;
        }
        if (bik.length != 9 || accountNumber.length != 20) {
            return false;
        }

        const allowedBicBase: readonly string[] = ["000", "001", "002"];
        const coefs = {
            0: 7,
            1: 1,
            2: 3,
        };

        const conditionNumber = allowedBicBase.includes(bik.slice(-3)) ? "0" + bik.substring(4, 2) : bik.slice(-3);
        const extendenedAccountNumber = conditionNumber + accountNumber;

        const totalCheckSum = extendenedAccountNumber.split("").reduce((sum, number, index) => {
            const coefOrder = index % 3;
            const x = coefs[coefOrder as keyof typeof coefs] * parseInt(number, 10);
            sum += x % 10;
            return sum;
        }, 0);

        return totalCheckSum % 10 == 0;
    }

    public static isValidMirCardNumber(cardNumber: string | undefined): boolean {
        if (isNullOrWhiteSpace(cardNumber)) {
            return false;
        } else if (cardNumber.length < 16) {
            return false;
        }

        return this.LuhnAlgorithmCheckSumVerifier(cardNumber);
    }

    public static isSnils(snils: string): boolean {
        if (!snils.length) {
            return false;
        } else if (/[^0-9]/.test(snils)) {
            return false; // СНИЛС может состоять только из цифр
        } else if (snils.length !== 11) {
            return false; // СНИЛС может состоять только из 11 цифр
        } else {
            let sum = 0;
            for (let i = 0; i < 9; i++) {
                const currentDigit = snils[i];
                if (isNotNullOrEmpty(currentDigit)) {
                    sum += parseInt(currentDigit, 10) * (9 - i);
                }
            }
            let checkDigit = 0;
            if (sum < 100) {
                checkDigit = sum;
            } else if (sum > 101) {
                checkDigit = parseInt((sum % 101).toString(), 10);
                if (checkDigit === 100) {
                    checkDigit = 0;
                }
            }
            if (checkDigit === parseInt(snils.slice(-2), 10)) {
                return true;
            }
        }
        return false;
    }

    public static isRegNumSfr(regNum: string): boolean {
        if (!regNum.length) {
            return false;
        } else if (/[^0-9]/.test(regNum)) {
            return false; // может состоять только из цифр
        } else if (regNum.length !== 10) {
            return false; // может состоять только из 10 цифр
        } else if (regNum.startsWith("0")) {
            return false; // не может начинаться с лидирующего нуля
        } else {
            const coefs = [2, 1, 2, 1, 2, 1, 2, 1, 2, 1];
            let sum = 0;
            for (let i = 0; i < 10; i++) {
                const currentDigit = regNum[i];
                if (isNotNullOrEmpty(currentDigit)) {
                    const typedCurrentDigit = parseInt(currentDigit, 10);
                    // @ts-ignore
                    const coefMul = typedCurrentDigit * coefs[i];
                    sum += coefMul > 9 ? coefMul - 9 : coefMul;
                }
            }
            if (sum % 10 === 0) {
                return true;
            }
        }
        return false;
    }

    public static isKpp(str: string): boolean {
        if (str.length !== 9) {
            return false;
        } else if (!/([0-9]{1}[1-9]{1}|[1-9]{1}[0-9]{1})([0-9]{2})([0-9A-Z]{2})([0-9]{3})/.test(str)) {
            return false;
        }

        return true;
    }
    public static isOkved(str: string): boolean {
        if (str.length < 2 || str.length > 8) {
            return false;
        } else if (
            !/[0-9]{2}/.test(str) &&
            !/[0-9]{2}\.[0-9]{1}/.test(str) &&
            !/[0-9]{2}\.[0-9]{2}/.test(str) &&
            !/[0-9]{2}\.[0-9]{2}\.[0-9]{1}/.test(str) &&
            !/[0-9]{2}\.[0-9]{2}\.[0-9]{2}/.test(str)
        ) {
            return false;
        }

        return true;
    }

    private static LuhnAlgorithmCheckSumVerifier(value: string) {
        const numbers = value.split("").map(num => parseInt(num, 10));

        const totalCheckSum = numbers.reduce((sum, number, index) => {
            let cardNumber = number;
            if ((numbers.length - index) % 2 == 0) {
                cardNumber *= 2;

                if (cardNumber > 9) {
                    cardNumber -= 9;
                }
            }

            sum += cardNumber;
            return sum;
        }, 0);

        return totalCheckSum % 10 === 0;
    }
}

const PHYS_OGRN_LENGTH = 13;
const IP_OGRN_LENGTH = 15;
const ogrnPattern = /^\d+$/;
